import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import Chip from 'tio-ui/components/chip';
import SectionHeader from 'tio-ui/components/section-header';
import Card from 'tio-ui/components/card';
import Chart from 'tio-ui/components/chart';
import DonutChart from 'tio-ui/components/charts/donut-chart';
import PieChart from 'tio-ui/components/charts/pie-chart';
import BarChart from 'tio-ui/components/charts/bar-chart';
import HorizontalBarChart from 'tio-ui/components/charts/horizontal-bar-chart';
import LineChart from 'tio-ui/components/charts/line-chart';
import AreaChart from 'tio-ui/components/charts/area-chart';
import SummaryDisplay from 'tio-ui/components/charts/summary-display';
import {
  donutSample,
  barSample,
  horizontalBarSample,
  lineSample,
  areaSample,
  pieSample,
  summarySample,
  smallSample,
} from 'tio-ui/utils/charts/chart-demo-data';
import {
  PhArrowLeft,
  PhChartBar,
  PhChartDonut,
  PhChartLineUp,
  PhChartPieSlice,
  PhCube,
  PhHashStraight,
  PhSquareHalfBottom,
  PhWaveSawtooth,
} from 'ember-phosphor-icons';

const handleClick = (data: { category: string; value: number }) => {
  console.log('Chart click:', data);
};

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    {{! Back navigation }}
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="tio-inline-row gap-2 tio-fg-info hover:tio-fg-info-h font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    {{! Header }}
    <PageHero
      @title="Charts"
      @subtitle="Native Canvas/SVG chart components — no Vega dependency. 7 chart types with dark mode, responsive sizing, tooltips, and click handlers."
      @icon={{PhChartDonut}}
      @gradient="cool"
      @decoration="dots"
    >
      <:actions>
        <Chip @intent="primary">
          <PhChartBar @size={{14}} class="mr-1" />
          7 Chart Types
        </Chip>
        <Chip @intent="success">Native Canvas/SVG</Chip>
      </:actions>
    </PageHero>

    {{! ================================================================
          Donut Chart
          ================================================================ }}
    <SectionHeader
      @icon={{PhChartDonut}}
      @title="DonutChart"
      @subtitle="SVG-based ring chart with interactive arcs and legend"
    />
    <Card>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <p class="text-sm tio-fg-muted mb-4">Default (4 items)</p>
          <DonutChart @data={{donutSample}} @onClick={{handleClick}} />
        </div>
        <div>
          <p class="text-sm tio-fg-muted mb-4">2 items, custom colors</p>
          <DonutChart @data={{smallSample}} @colors={{twoColors}} @onClick={{handleClick}} />
        </div>
      </div>

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>&lt;DonutChart
            @data=&#123;&#123;this.data&#125;&#125;
            @onClick=&#123;&#123;this.handleClick&#125;&#125; /&gt; &lt;!-- Or via unified Chart
            wrapper --&gt; &lt;Chart @data=&#123;&#123;this.data&#125;&#125; @type="donut"
            @onClick=&#123;&#123;this.handleClick&#125;&#125; /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Bar Chart
          ================================================================ }}
    <SectionHeader
      @icon={{PhChartBar}}
      @title="BarChart"
      @subtitle="Canvas-rendered vertical bar chart with auto-rotating labels"
    />
    <Card>
      <BarChart
        @data={{barSample}}
        @xLabel="Month"
        @yLabel="Applications"
        @onClick={{handleClick}}
      />

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>&lt;BarChart
            @data=&#123;&#123;this.data&#125;&#125; @xLabel="Month" @yLabel="Count" /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Horizontal Bar Chart
          ================================================================ }}
    <SectionHeader
      @icon={{PhSquareHalfBottom}}
      @title="HorizontalBarChart"
      @subtitle="Canvas-rendered horizontal bars, auto-sorted by value"
    />
    <Card>
      <HorizontalBarChart @data={{horizontalBarSample}} @onClick={{handleClick}} />

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code
          >&lt;HorizontalBarChart @data=&#123;&#123;this.data&#125;&#125;
            @sorted=&#123;&#123;true&#125;&#125; /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Line Chart
          ================================================================ }}
    <SectionHeader
      @icon={{PhChartLineUp}}
      @title="LineChart"
      @subtitle="Canvas-rendered line chart with point markers and crosshairs"
    />
    <Card>
      <LineChart
        @data={{lineSample}}
        @xLabel="Quarter"
        @yLabel="Revenue ($)"
        @onClick={{handleClick}}
      />

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>&lt;LineChart
            @data=&#123;&#123;this.data&#125;&#125; @showPoints=&#123;&#123;true&#125;&#125; /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Area Chart
          ================================================================ }}
    <SectionHeader
      @icon={{PhWaveSawtooth}}
      @title="AreaChart"
      @subtitle="Canvas-rendered area chart with gradient fill"
    />
    <Card>
      <AreaChart
        @data={{areaSample}}
        @xLabel="Day"
        @yLabel="Active Users"
        @onClick={{handleClick}}
      />

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>&lt;AreaChart
            @data=&#123;&#123;this.data&#125;&#125; @fillOpacity=&#123;&#123;0.3&#125;&#125; /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Pie Chart
          ================================================================ }}
    <SectionHeader
      @icon={{PhChartPieSlice}}
      @title="PieChart"
      @subtitle="SVG-based pie chart with hover effects"
    />
    <Card>
      <div class="max-w-md mx-auto">
        <PieChart @data={{pieSample}} @onClick={{handleClick}} />
      </div>

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>&lt;PieChart
            @data=&#123;&#123;this.data&#125;&#125;
            @onClick=&#123;&#123;this.handleClick&#125;&#125; /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Summary Display
          ================================================================ }}
    <SectionHeader
      @icon={{PhHashStraight}}
      @title="SummaryDisplay"
      @subtitle="Large number display for KPI metrics"
    />
    <Card>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryDisplay @data={{summarySample}} @label="Total Employees" />
        <SummaryDisplay
          @data={{donutSample}}
          @label="Total Participants"
          @valueColor="tio-demo-chart-value"
        />
        <SummaryDisplay @data={{smallSample}} @label="Completion Rate" @valueColor="tio-fg-brand" />
      </div>

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>&lt;SummaryDisplay
            @data=&#123;&#123;this.data&#125;&#125; @label="Total Employees" /&gt;</code></pre>
      </details>
    </Card>

    {{! ================================================================
          Unified Chart Wrapper
          ================================================================ }}
    <SectionHeader
      @icon={{PhCube}}
      @title="Unified Chart Wrapper"
      @subtitle="Single <Chart> component that dispatches to any chart type"
    />
    <Card>
      <p class="text-sm tio-fg-muted mb-4">
        The unified
        <code class="px-1 py-0.5 tio-bg-sunken rounded text-xs">Chart</code>
        component is a drop-in replacement for the Vega
        <code class="px-1 py-0.5 tio-bg-sunken rounded text-xs">setupChart</code>
        modifier. Just set
        <code class="px-1 py-0.5 tio-bg-sunken rounded text-xs">@type</code>
        to any chart type.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <p class="text-sm tio-fg-muted mb-2">type="donut"</p>
          <Chart @data={{donutSample}} @type="donut" @onClick={{handleClick}} />
        </div>
        <div>
          <p class="text-sm tio-fg-muted mb-2">type="bar"</p>
          <Chart @data={{barSample}} @type="bar" @onClick={{handleClick}} @height={{200}} />
        </div>
      </div>

      <details class="mt-6">
        <summary class="tio-text-sm-medium tio-fg-secondary cursor-pointer">Usage</summary>
        <pre class="mt-2 p-3 tio-surface-sunken text-xs overflow-x-auto"><code>import Chart from
            'tio-ui/components/chart'; &lt;Chart @data=&#123;&#123;this.data&#125;&#125;
            @type="donut" @onClick=&#123;&#123;this.handleClick&#125;&#125; /&gt; &lt;Chart
            @data=&#123;&#123;this.data&#125;&#125; @type="bar" @xLabel="Month" /&gt; &lt;Chart
            @data=&#123;&#123;this.data&#125;&#125; @type="line"
            @showPoints=&#123;&#123;true&#125;&#125; /&gt; &lt;Chart
            @data=&#123;&#123;this.data&#125;&#125; @type="summary" @label="Total" /&gt;</code></pre>
      </details>
    </Card>
  </VStack>
</template>

const twoColors = ['#3B82F6', '#10B981'];
